// src/components/data_visualizations/ReasonsNotChosenHeatmap.js
import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Grid, Dialog, DialogTitle, DialogContent, IconButton, Stack, Divider, Link as MuiLink
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';

/* ------------------------- helpers ------------------------- */

// Build unique lists safely
function uniq(arr) {
  return Array.from(new Set(arr.filter(Boolean)));
}

// Gather all matching reason objects for (solutionCategory, reasonCategory)
function collectMatches(data, solutionCategory, reasonCategory) {
  const matches = [];

  for (const item of data || []) {
    const solutions = Array.isArray(item?.considered_solutions) ? item.considered_solutions : [];
    for (const sol of solutions) {
      if (sol?.considered_solution_category !== solutionCategory) continue;
      const reasons = Array.isArray(sol?.reasons_not_chosen) ? sol.reasons_not_chosen : [];
      for (const r of reasons) {
        if (r?.reason_not_chosen_category !== reasonCategory) continue;
        matches.push({
          // required fields (as requested)
          reason_not_chosen: r?.reason_not_chosen ?? r?.reason ?? r?.text ?? '',
          reason_not_chosen_importance: r?.reason_not_chosen_importance ?? null,
          reason_not_chosen_importance_justification:
            r?.reason_not_chosen_importance_justification ?? '',
          reason_not_chosen_quote: r?.reason_not_chosen_quote ?? '',
          // optional helpful context we won't render unless needed later:
          _story: item,
          _solution: sol,
        });
      }
    }
  }

  return matches;
}

// Stats for a given cell
function calcCellStats(data, solutionCategory, reasonCategory) {
  const matches = collectMatches(data, solutionCategory, reasonCategory);
  const count = matches.length;

  if (!count) {
    return { average: null, count: 0, score: null, instances: [] };
  }

  const sum = matches.reduce(
    (acc, r) => acc + (Number(r.reason_not_chosen_importance) || 0),
    0
  );
  const average = sum / count;
  const score = count * average; // Consideration score

  return { average, count, score, instances: matches };
}

// Color mapping by score (light → bright red)
function colorByScore(score, min, max) {
  if (score === null) return '#D3D3D3';
  if (max <= min) return 'rgb(255, 245, 245)'; // safe fallback when range collapses

  const intensity = (score - min) / (max - min); // [0..1]
  const red = 255;
  const green = 255 - Math.round(intensity * 255);
  const blue = 255 - Math.round(intensity * 255);
  return `rgb(${red}, ${green}, ${blue})`;
}

/* ------------------------- component ------------------------- */

const ReasonsNotChosenHeatmap = ({ filteredData = [] }) => {
  // X axis: considered_solution_category
  const solutionCategories = useMemo(
    () =>
      uniq(
        filteredData.flatMap((item) =>
          (item?.considered_solutions || []).map((s) => s?.considered_solution_category)
        )
      ),
    [filteredData]
  );

  // Y axis: reason_not_chosen_category
  const reasonCategories = useMemo(
    () =>
      uniq(
        filteredData.flatMap((item) =>
          (item?.considered_solutions || []).flatMap((s) =>
            (s?.reasons_not_chosen || []).map((r) => r?.reason_not_chosen_category)
          )
        )
      ),
    [filteredData]
  );

  // Precompute a matrix of stats and the global min/max of the Consideration score
  const { matrix, minScore, maxScore } = useMemo(() => {
    const m = new Map(); // key: `${r}|||${s}` -> { average, count, score, instances }
    let min = Infinity;
    let max = -Infinity;

    for (const r of reasonCategories) {
      for (const s of solutionCategories) {
        const key = `${r}|||${s}`;
        const stats = calcCellStats(filteredData, s, r);
        m.set(key, stats);
        if (stats.score !== null) {
          if (stats.score < min) min = stats.score;
          if (stats.score > max) max = stats.score;
        }
      }
    }

    if (!Number.isFinite(min)) min = 0;
    if (!Number.isFinite(max)) max = 0;

    return { matrix: m, minScore: min, maxScore: max };
  }, [filteredData, reasonCategories, solutionCategories]);

  // Modal state
  const [open, setOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalItems, setModalItems] = useState([]);

  const openModal = (solution, reason) => {
    const key = `${reason}|||${solution}`;
    const stats = matrix.get(key) || { instances: [] };
    setModalTitle(`${reason} • ${solution}`);
    setModalItems(stats.instances || []);
    setOpen(true);
  };
  const closeModal = () => setOpen(false);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Considered Solutions × Reasons Not Chosen
      </Typography>
      <Typography variant="body2" gutterBottom>
        Cell color uses <strong>Consideration score</strong> = <em>interviews count × average importance</em>.
        Below the score, each cell shows the interviews count and the average importance.
      </Typography>
      <br />

      {/* Header Row */}
      <Grid container>
        <Grid item xs={2} />
        {solutionCategories.map((solution, i) => (
          <Grid item xs key={`hdr-${i}`}>
            <Typography variant="subtitle2" align="center">
              {solution}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Data Rows */}
      {reasonCategories.map((reasonCategory, rIdx) => (
        <Grid container key={`row-${rIdx}`}>
          <Grid item xs={2}>
            <Typography variant="subtitle2">{reasonCategory}</Typography>
          </Grid>

          {solutionCategories.map((solutionCategory, cIdx) => {
            const key = `${reasonCategory}|||${solutionCategory}`;
            const stats = matrix.get(key) || { average: null, count: 0, score: null };
            const bg = colorByScore(stats.score, minScore, maxScore);

            return (
              <Grid
                item
                xs
                key={`cell-${rIdx}-${cIdx}`}
                onClick={() => stats.score !== null && openModal(solutionCategory, reasonCategory)}
                style={{
                  backgroundColor: bg,
                  border: '1px solid #ccc',
                  padding: '8px',
                  textAlign: 'center',
                  cursor: stats.score !== null ? 'pointer' : 'default',
                  userSelect: 'none'
                }}
              >
                {stats.score !== null ? (
                  <>
                    {/* Primary metric: Consideration score */}
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Score: {stats.score.toFixed(0)}
                    </Typography>
                    {/* Secondary: count + avg */}
                    <Typography variant="caption" display="block">
                      {stats.count} {stats.count === 1 ? 'interview' : 'interviews'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {stats.average.toFixed(1)} importance
                    </Typography>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    n/a
                  </Typography>
                )}
              </Grid>
            );
          })}
        </Grid>
      ))}

      {/* Modal with all matching reasons_not_chosen objects */}
      <Dialog open={open} onClose={closeModal} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          {modalTitle}
          <IconButton
            aria-label="close"
            onClick={closeModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {modalItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No matching interviews found.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {modalItems.map((it, idx) => (
                <Box key={`inst-${idx}`} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2">Reason Not Chosen: {it.reason_not_chosen || '—'}</Typography>

                    <Divider sx={{ my: 1 }} />

                    <Typography variant="subtitle2">Importance: {it.reason_not_chosen_importance != null ? String(it.reason_not_chosen_importance) : '—'}</Typography>

                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Importance Justification:
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                      {it.reason_not_chosen_importance_justification || '—'}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Quote:
                    </Typography>
                    {it.reason_not_chosen_quote ? (
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <OndemandVideoIcon fontSize="small" />
                        <MuiLink href="#" underline="hover">
                          {it.reason_not_chosen_quote}
                        </MuiLink>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReasonsNotChosenHeatmap;
